// Ethereum signature helpers with zero dependencies (works in Node and the browser):
// keccak256, secp256k1 sign/recover, EIP-191 personal_sign hashing, EIP-55 addresses.

// ---------- keccak256 ----------
const RC = [
  0x0000000000000001n, 0x0000000000008082n, 0x800000000000808An, 0x8000000080008000n,
  0x000000000000808Bn, 0x0000000080000001n, 0x8000000080008081n, 0x8000000000008009n,
  0x000000000000008An, 0x0000000000000088n, 0x0000000080008009n, 0x000000008000000An,
  0x000000008000808Bn, 0x800000000000008Bn, 0x8000000000008089n, 0x8000000000008003n,
  0x8000000000008002n, 0x8000000000000080n, 0x000000000000800An, 0x800000008000000An,
  0x8000000080008081n, 0x8000000000008080n, 0x0000000080000001n, 0x8000000080008008n,
];
const ROT = [0, 1, 62, 28, 27, 36, 44, 6, 55, 20, 3, 10, 43, 25, 39, 41, 45, 15, 21, 8, 18, 2, 61, 56, 14];
const M64 = (1n << 64n) - 1n;
const rotl = (v, n) => (n === 0 ? v : ((v << BigInt(n)) | (v >> BigInt(64 - n))) & M64);

function keccakF(A) {
  const C = new Array(5), B = new Array(25);
  for (let round = 0; round < 24; round++) {
    for (let x = 0; x < 5; x++) C[x] = A[x] ^ A[x + 5] ^ A[x + 10] ^ A[x + 15] ^ A[x + 20];
    for (let x = 0; x < 5; x++) {
      const D = C[(x + 4) % 5] ^ rotl(C[(x + 1) % 5], 1);
      for (let y = 0; y < 25; y += 5) A[x + y] ^= D;
    }
    for (let x = 0; x < 5; x++) {
      for (let y = 0; y < 5; y++) {
        B[y + 5 * ((2 * x + 3 * y) % 5)] = rotl(A[x + 5 * y], ROT[x + 5 * y]);
      }
    }
    for (let y = 0; y < 25; y += 5) {
      for (let x = 0; x < 5; x++) A[x + y] = B[x + y] ^ (~B[((x + 1) % 5) + y] & M64 & B[((x + 2) % 5) + y]);
    }
    A[0] ^= RC[round];
  }
}

export function keccak256(bytes) {
  const rate = 136;
  const padLen = rate - (bytes.length % rate);
  const data = new Uint8Array(bytes.length + padLen);
  data.set(bytes);
  data[bytes.length] ^= 0x01;
  data[data.length - 1] ^= 0x80;
  const A = new Array(25).fill(0n);
  for (let off = 0; off < data.length; off += rate) {
    for (let i = 0; i < rate / 8; i++) {
      let lane = 0n;
      for (let b = 7; b >= 0; b--) lane = (lane << 8n) | BigInt(data[off + i * 8 + b]);
      A[i] ^= lane;
    }
    keccakF(A);
  }
  const out = new Uint8Array(32);
  for (let i = 0; i < 4; i++) {
    let lane = A[i];
    for (let b = 0; b < 8; b++) { out[i * 8 + b] = Number(lane & 0xffn); lane >>= 8n; }
  }
  return out;
}

// ---------- byte helpers ----------
export const utf8 = (s) => new TextEncoder().encode(s);
export const toHex = (b) => Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
export function fromHex(h) {
  h = h.startsWith('0x') ? h.slice(2) : h;
  if (h.length % 2 || /[^0-9a-fA-F]/.test(h)) throw new Error('bad hex');
  const out = new Uint8Array(h.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(h.substr(i * 2, 2), 16);
  return out;
}
const bytesToBig = (b) => (b.length ? BigInt('0x' + toHex(b)) : 0n);
const bigTo32 = (n) => fromHex(n.toString(16).padStart(64, '0'));

// ---------- secp256k1 ----------
const P = 0xFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFEFFFFFC2Fn;
const N = 0xFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFEBAAEDCE6AF48A03BBFD25E8CD0364141n;
const G = [
  0x79BE667EF9DCBBAC55A06295CE870B07029BFCDB2DCE28D959F2815B16F81798n,
  0x483ADA7726A3C4655DA4FBFC0E1108A8FD17B448A68554199C47D08FFB10D4B8n,
];
const mod = (a, m = P) => { const r = a % m; return r >= 0n ? r : r + m; };
function inv(a, m = P) {
  let [lo, hi, x0, x1] = [mod(a, m), m, 1n, 0n];
  while (lo > 1n) { const q = hi / lo; [lo, hi] = [hi - q * lo, lo]; [x0, x1] = [x1 - q * x0, x0]; }
  if (lo === 0n) throw new Error('no inverse');
  return mod(x0, m);
}
function powmod(b, e, m = P) {
  let r = 1n; b = mod(b, m);
  while (e > 0n) { if (e & 1n) r = (r * b) % m; b = (b * b) % m; e >>= 1n; }
  return r;
}

// Jacobian points [X, Y, Z]; Z = 0n is infinity.
const INF = [0n, 1n, 0n];
function jDouble([X, Y, Z]) {
  if (Z === 0n || Y === 0n) return INF;
  const YY = mod(Y * Y), S = mod(4n * X * YY), M = mod(3n * X * X);
  const X3 = mod(M * M - 2n * S);
  return [X3, mod(M * (S - X3) - 8n * YY * YY), mod(2n * Y * Z)];
}
function jAdd(p, q) {
  if (p[2] === 0n) return q;
  if (q[2] === 0n) return p;
  const [X1, Y1, Z1] = p, [X2, Y2, Z2] = q;
  const Z1Z1 = mod(Z1 * Z1), Z2Z2 = mod(Z2 * Z2);
  const U1 = mod(X1 * Z2Z2), U2 = mod(X2 * Z1Z1);
  const S1 = mod(Y1 * Z2 * Z2Z2), S2 = mod(Y2 * Z1 * Z1Z1);
  if (U1 === U2) return S1 === S2 ? jDouble(p) : INF;
  const H = mod(U2 - U1), R = mod(S2 - S1), HH = mod(H * H), HHH = mod(H * HH);
  const X3 = mod(R * R - HHH - 2n * U1 * HH);
  return [X3, mod(R * (U1 * HH - X3) - S1 * HHH), mod(H * Z1 * Z2)];
}
function jMul(pt, k) {
  let r = INF, a = pt;
  while (k > 0n) { if (k & 1n) r = jAdd(r, a); a = jDouble(a); k >>= 1n; }
  return r;
}
function toAffine([X, Y, Z]) {
  if (Z === 0n) return null;
  const zi = inv(Z), zi2 = mod(zi * zi);
  return [mod(X * zi2), mod(Y * zi2 * zi)];
}
const GJ = [G[0], G[1], 1n];

function pubToAddressBytes([x, y]) {
  const pub = new Uint8Array(64);
  pub.set(bigTo32(x), 0); pub.set(bigTo32(y), 32);
  return keccak256(pub).slice(12);
}

export function toChecksumAddress(addr) {
  const a = addr.toLowerCase().replace(/^0x/, '');
  if (!/^[0-9a-f]{40}$/.test(a)) throw new Error('bad address');
  const h = toHex(keccak256(utf8(a)));
  let out = '0x';
  for (let i = 0; i < 40; i++) out += parseInt(h[i], 16) >= 8 ? a[i].toUpperCase() : a[i];
  return out;
}

export function privateKeyToAddress(privHex) {
  const d = bytesToBig(fromHex(privHex));
  if (d <= 0n || d >= N) throw new Error('bad private key');
  return toChecksumAddress(toHex(pubToAddressBytes(toAffine(jMul(GJ, d)))));
}

export function hashPersonalMessage(message) {
  const m = utf8(message);
  const prefix = utf8(`\x19Ethereum Signed Message:\n${m.length}`);
  const all = new Uint8Array(prefix.length + m.length);
  all.set(prefix); all.set(m, prefix.length);
  return keccak256(all);
}

function randomScalar() {
  const b = new Uint8Array(32);
  for (;;) {
    globalThis.crypto.getRandomValues(b);
    const k = bytesToBig(b);
    if (k > 0n && k < N) return k;
  }
}

export function randomPrivateKey() { return '0x' + bigTo32(randomScalar()).reduce((s, x) => s + x.toString(16).padStart(2, '0'), ''); }

// personal_sign equivalent. Returns 0x-prefixed 65-byte hex signature (r||s||v, v = 27/28).
export function signPersonalMessage(privHex, message) {
  const d = bytesToBig(fromHex(privHex));
  const e = mod(bytesToBig(hashPersonalMessage(message)), N);
  for (;;) {
    const k = randomScalar();
    const R = toAffine(jMul(GJ, k));
    const r = mod(R[0], N);
    if (r === 0n) continue;
    let s = mod(inv(k, N) * (e + r * d), N);
    if (s === 0n) continue;
    let recid = Number(R[1] & 1n);
    if (s > N / 2n) { s = N - s; recid ^= 1; }
    return '0x' + toHex(bigTo32(r)) + toHex(bigTo32(s)) + (27 + recid).toString(16);
  }
}

// Returns checksummed signer address, or throws if the signature is malformed.
export function recoverPersonalSigner(message, sigHex) {
  const sig = fromHex(sigHex);
  if (sig.length !== 65) throw new Error('signature must be 65 bytes');
  const r = bytesToBig(sig.slice(0, 32)), s = bytesToBig(sig.slice(32, 64));
  let v = sig[64];
  if (v >= 27) v -= 27;
  if (v !== 0 && v !== 1) throw new Error('bad recovery id');
  if (r <= 0n || r >= N || s <= 0n || s >= N) throw new Error('signature out of range');
  if (s > N / 2n) throw new Error('high-s signature rejected');
  const x = r;
  const y2 = mod(x * x * x + 7n);
  let y = powmod(y2, (P + 1n) / 4n);
  if (mod(y * y) !== y2) throw new Error('invalid signature point');
  if (Number(y & 1n) !== v) y = P - y;
  const e = mod(bytesToBig(hashPersonalMessage(message)), N);
  const rInv = inv(r, N);
  const u1 = mod(-e * rInv, N), u2 = mod(s * rInv, N);
  const Q = toAffine(jAdd(jMul(GJ, u1), jMul([x, y, 1n], u2)));
  if (!Q) throw new Error('invalid signature');
  return toChecksumAddress(toHex(pubToAddressBytes(Q)));
}
